import { useNavigate } from "react-router-dom";
import { FileText, FileSignature, MapPin, RefreshCcw, MessageSquareText, TrendingUp, Briefcase, Building2, FolderCheck, ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { DOCS_IMG } from "../lib/constants";

const services = [
  { icon: FileText, title: "Déclaration fiscale suisse", desc: "Préparation et dépôt complet de votre déclaration d'impôt suisse, dans tous les cantons, avec optimisation des déductions autorisées." },
  { icon: FileSignature, title: "Déclaration fiscale française", desc: "Déclaration de revenus française incluant revenus suisses, revenus fonciers, comptes à l'étranger et crédits d'impôt." },
  { icon: MapPin, title: "Fiscalité des frontaliers", desc: "Accompagnement complet des travailleurs frontaliers Suisse-France : obligations des deux pays, statut fiscal et déductions." },
  { icon: RefreshCcw, title: "Rectification d'impôt à la source", desc: "Demande de rectification (TOU / quasi-résident) pour récupérer les déductions auxquelles vous avez droit à Genève et dans les autres cantons." },
  { icon: MessageSquareText, title: "Conseils fiscaux personnalisés", desc: "Analyse de votre situation et recommandations claires, conformes et adaptées à vos objectifs." },
  { icon: TrendingUp, title: "Optimisation fiscale", desc: "Identification des leviers d'optimisation légaux : 3e pilier, rachats LPP, frais réels, déductions familiales et immobilières." },
  { icon: Briefcase, title: "Accompagnement des indépendants en Suisse", desc: "Suivi fiscal et administratif des indépendants : comptabilité de base, TVA, charges et déclaration annuelle." },
  { icon: Building2, title: "Fiscalité des entreprises suisses", desc: "Déclarations, suivi fiscal et optimisation pour les sociétés basées en Suisse (Sàrl, SA, raison individuelle)." },
  { icon: FolderCheck, title: "Préparation, vérification et suivi des dossiers fiscaux", desc: "Constitution des justificatifs, vérification des taxations reçues et suivi de vos dossiers auprès des administrations." },
];

export default function Services() {
  const navigate = useNavigate();

  return (
    <main data-testid="services-page" className="pt-[76px]">
      <SEO
        title="Services fiscaux | Déclaration fiscale suisse et française — Fiscoconseils"
        description="Déclaration fiscale suisse, déclaration française, rectification impôt à la source, conseil fiscal frontalier, optimisation fiscale, fiscalité indépendant et entreprise suisse."
      />

      <section className="relative bg-[#0A1128] overflow-hidden">
        <img src={DOCS_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-4">Nos services</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white max-w-3xl leading-[1.05]">
            Des services fiscaux complets, de la déclaration à l'optimisation
          </h1>
          <p className="text-base md:text-lg text-[#D1D5DB] mt-6 max-w-2xl leading-relaxed">
            Fiscalité suisse, fiscalité française, frontaliers, indépendants et entreprises : une expertise transversale pour chaque besoin.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <div
              key={s.title}
              data-testid={`service-card-${i}`}
              className="bg-white border border-[#C4C8CC]/50 rounded-2xl p-8 shadow-[0_8px_32px_rgba(10,17,40,0.06)] hover:-translate-y-1 transition-transform duration-300"
            >
              <div className="w-12 h-12 rounded-full bg-[#F8F9FA] border border-[#E5E7EB] flex items-center justify-center">
                <s.icon size={22} strokeWidth={1.5} className="text-[#0A1128]" />
              </div>
              <h2 className="text-lg font-bold text-[#0A1128] mt-6">{s.title}</h2>
              <p className="text-sm text-[#6B7280] mt-3 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-20">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#6B7280] mb-4">Tarifs transparents</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A1128] max-w-2xl">
            Des prestations claires, des prix sans surprise
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {[
              { price: "Gratuit", label: "Simulation fiscale", desc: "Estimation de votre situation, sans engagement." },
              { price: "dès CHF 100.-", label: "Déclaration fiscale suisse", desc: "Préparation et dépôt de votre déclaration suisse." },
              { price: "dès 100 €", label: "Déclaration fiscale française", desc: "Déclaration de revenus française, revenus suisses inclus." },
              { price: "dès CHF 200.-", label: "Conseils fiscaux", desc: "Analyse personnalisée et recommandations d'expert." },
            ].map((t, i) => (
              <div key={t.label} data-testid={`pricing-card-${i}`} className="bg-white border border-[#C4C8CC]/50 rounded-2xl p-8 shadow-[0_8px_32px_rgba(10,17,40,0.06)] hover:-translate-y-1 transition-transform duration-300">
                <p className="font-heading text-3xl font-semibold text-[#0A1128]">{t.price}</p>
                <h3 className="text-base font-bold text-[#0A1128] mt-4">{t.label}</h3>
                <p className="text-sm text-[#6B7280] mt-2 leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#6B7280] mt-6">Tarifs indicatifs selon la complexité du dossier. Un devis précis vous est communiqué après analyse de votre situation.</p>
        </div>

        <div className="mt-20 bg-[#F8F9FA] border border-[#E5E7EB] rounded-2xl p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[#0A1128]">Quel service correspond à votre situation ?</h2>
            <p className="text-base text-[#6B7280] mt-3 max-w-xl">Remplissez notre formulaire intelligent : il s'adapte à votre profil et nous permet de vous proposer un devis précis.</p>
          </div>
          <button
            data-testid="services-devis-btn"
            onClick={() => navigate("/devis")}
            className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#0A1128] rounded-full px-8 py-4 hover:bg-[#1A2342] transition-colors duration-200 shrink-0"
          >
            Demander un devis <ArrowRight size={16} />
          </button>
        </div>
      </section>
    </main>
  );
}
